import { FunctionComponent, useState, useMemo } from 'react';
import DashboardLayout from '../../../components/layout/layout';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useGetCPUploadDate } from '../../../hooks/api/otherextranet';

type CPUploadDate = {
  repName:string;
  freshDate:string;
};

type SortField = 'freshDate' | 'repName';
type SortDirection = 'asc' | 'desc';

const ManageChannelPartnerUploadDate: FunctionComponent = () => {
  const navigate = useNavigate();
  // Removed pagination state variables
  const [ sortField, setSortField ] = useState<SortField>('freshDate');
  const [ sortDirection, setSortDirection ] = useState<SortDirection>('desc');
  const { data: CP_UploadDate, isFetched: hasFetched } = useGetCPUploadDate();

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for a column
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortDirection === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortDirection === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  // Sort all data client-side (removed pagination)
  const sortedData = useMemo(() => {
    if (!CP_UploadDate?.length) return [];
    
    return [...CP_UploadDate].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      // Handle date sorting
      if (sortField === 'freshDate') {
        if (!aValue) aValue = '1900-01-01'; // Put null dates at the end
        if (!bValue) bValue = '1900-01-01';
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // Handle string sorting
      if (sortField === 'repName') {
        if (!aValue) aValue = '';
        if (!bValue) bValue = '';
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [CP_UploadDate, sortField, sortDirection]);

  return(
    <DashboardLayout
        titleIcon={<DesktopWindowsOutlinedIcon />}
        title="Other Extranet"
        initialBreadcrumbs="Extranet/Barksdale Admin"
        levelBreadcrumbs=""
        breadcrumbs="Channel Partner List"
        activeMenu="overview"
        containerTitle={"Manage Channel Partners"}
    >
      <Box sx={{ pt: 0, px: 0, pb: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={0}
        >
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            Upload Dates
          </Typography>
        </Box>    
      </Box>
        
      <Box sx={{ width: '100%', maxWidth: '100%', mt: 2 }}>
        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0, mt: 3 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
              <TableRow>
                <TableCell 
                  sx={{ 
                    cursor: 'pointer', 
                    '&:hover': { backgroundColor: '#e0e0e0' },
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('freshDate')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Upload
                    <Tooltip title="Click to sort">{getSortIcon('freshDate')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ 
                    cursor: 'pointer', 
                    '&:hover': { backgroundColor: '#e0e0e0' },
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('repName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Distributor
                    <Tooltip title="Click to sort">{getSortIcon('repName')}</Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                hasFetched ? (
                  sortedData.length === 0 ?
                    <TableRow>
                      <TableCell colSpan={2}>
                        No Data Found
                      </TableCell>
                    </TableRow>
                : sortedData.map((uploadDate: CPUploadDate, index: number) => (    
                  <TableRow key={`${uploadDate.repName}-${uploadDate.freshDate}-${index}`} hover>
                    <TableCell>{uploadDate.freshDate ? moment(uploadDate.freshDate).format('MM/DD/YYYY') : ''}</TableCell>
                    <TableCell>{uploadDate.repName}</TableCell>
                  </TableRow>
                ))
                ) : (
                  <>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Skeleton width="38%" />
                        <Skeleton width="100%" />
                        <Skeleton width="100%" />
                        <Skeleton width="61%" />
                      </TableCell>
                    </TableRow>
                  </>
                ) 
              }      
            </TableBody>
          </Table>
        </TableContainer>

        {/* Back Button (removed pagination) */}
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
          <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={() => navigate(-1)}>
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
}

export default ManageChannelPartnerUploadDate;